import React from 'react';
import { Link } from 'react-router-dom';
import { Hidden } from '@material-ui/core';
import { useCompensationSetting } from '../../../components/use-compensation-setting';
import { ScheduledPayment } from './compensation-history-model';
import { post2018 } from './compensation-history-slice';
import { PaymentItemView } from './payment-item-view';
import { addMoney, zeroDollar } from '../../../../../global/helpers/math';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../../constants/routes';
import { getFormattedDate } from '../../../../../global/helpers/date-helpers';
import { compHistoryTableStyles } from '../../../../../global/constants/theme/styles/tables/comp-history-tables';
import { Pagination } from '../../../components/pagination';
import { No, Yes } from '../../../constants/constant-strings';

interface ScheduledPaymentViewProps {
    current: ScheduledPayment,
    next: ScheduledPayment,
    paginationName: string,
    totalPages: number,
    editDisabled: boolean,
}

export const ScheduledPaymentView = (props: ScheduledPaymentViewProps) => {
    const tableClasses = compHistoryTableStyles();
    const basePath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.Compensation}`);
    const settings = useCompensationSetting();
    const post2018Data = post2018(props.current?.effective_date);

    const getValue = (next: ScheduledPayment, key: string, handler?: (a) => string, defaultValue?: string) => {
        if (!next) {
            return null;
        }
        if (handler) {
            return next[key] ? handler(next[key]) : defaultValue;
        }

        return next[key] ?? zeroDollar;
    };

    const renderEdit = () => {
        return (
            <div className='paginated-table-edit-row'>
                <div className='paginated-table-row-cell paginated-table-edit-row-cell'>
                    <Link to={`${basePath}${ArcRoutes.ScheduledPayment}/${props.current?.id}`}>
                        {settings.editScheduledPaymentButton}
                    </Link>
                </div>
                {
                    props.next ?
                        <Hidden xsDown>
                            <div className='paginated-table-row-cell paginated-table-edit-row-cell'>
                                <Link to={`${basePath}${ArcRoutes.ScheduledPayment}/${props.next.id}`}>
                                    {settings.editScheduledPaymentButton}
                                </Link>
                            </div>
                        </Hidden>
                        : null
                }
            </div>
        );
    };
    return (
        <>


            <div className={tableClasses.paginatedTable}>
                <PaymentItemView
                    label={settings.effectiveDateLabel}
                    current={getFormattedDate(props?.current?.effective_date)}
                    next={getFormattedDate(props?.next?.effective_date)}
                />

                {
                    post2018Data ?

                        <PaymentItemView
                            label={settings.baseSalaryLabel}
                            current={getValue(props?.current, 'cash_salary')}
                            next={getValue(props?.next, 'cash_salary')}
                            tooltip={settings.baseSalaryLabelHelperText}
                            title={settings.scheduledPaymentsHeader}
                        />

                        :
                        <>
                            <PaymentItemView
                                label={settings.cashStipendLabel}
                                current={getValue(props?.current, 'cash_salary')}
                                next={getValue(props?.next, 'cash_salary')}
                                tooltip={settings.cashStipendHelperText}
                                title={settings.scheduledPaymentsHeader}
                            />

                        </>
                }
                {
                    post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.employerContributionLabel}
                                current={getValue(props?.current, 'er_plan_contributions')}
                                next={getValue(props?.next, 'er_plan_contributions')}
                                tooltip={settings.employerContributionLabelHelperText}
                            />

                        </> : null

                }
                {
                    post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.cashHousingAllowanceLabel}
                                current={addMoney(props?.current?.housing_cash_allowance, props?.current?.utilities)}
                                next={props.next ? addMoney(props?.next?.housing_cash_allowance, props?.next?.utilities) : null}
                                tooltip={settings.cashHousingAllowanceLabelHelperText}
                            />
                        </> : null

                }

                {
                    post2018Data ?

                        <PaymentItemView
                            label={settings.providedHousingLabel}
                            current={getValue(props?.current, 'housing_provided', (a: string) => { return a.toLowerCase() === 'housing' ? Yes : No; }, No)}
                            next={getValue(props?.next, 'housing_provided', (a: string) => { return a.toLowerCase() === 'housing' ? Yes : No; }, No)}
                            tooltip={settings.providedHousingLabelHelperText}
                            title={settings.housingHeader}
                        />
                        : <PaymentItemView
                            label={settings.churchProvidedHousingLabel}
                            current={getValue(props?.current, 'housing_provided', (a: string) => { return a.toLowerCase() === 'room_and_board' || a === 'housing' ? Yes : No; }, No)}
                            next={getValue(props?.next, 'housing_provided', (a: string) => { return a.toLowerCase() === 'room_and_board' || a === 'housing' ? Yes : No; }, No)}
                            tooltip={settings.churchProvidedHousingHelperText}
                            title={settings.housingHeader}
                        />

                }
                {
                    !post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.receiveMealsLabel}
                                current={getValue(props?.current, 'housing_provided', (a: string) => { return a.toLowerCase() === 'room_and_board' ? Yes : No; }, No)}
                                next={getValue(props?.next, 'housing_provided', (a: string) => { return a.toLowerCase() === 'room_and_board' ? Yes : No; }, No)}
                                tooltip={settings.receiveMealsHelperText}
                            />
                        </> : null

                }
                {
                    !post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.calculatedHousingLabel}
                                current={getValue(props.current, 'calculated_housing')}
                                next={getValue(props.next, 'calculated_housing')}
                                tooltip={settings.calculatedHousingHelperText}
                            />
                        </> : null

                }
                {
                    !post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.utilityLabel}
                                current={getValue(props?.current, 'utilities')}
                                next={getValue(props?.next, 'utilities')}
                                tooltip={settings.utilityHelperText}
                            />
                        </> : null

                }
                {
                    !post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.cashHousingAllowanceLabel}
                                current={getValue(props?.current, 'housing_cash_allowance')}
                                next={getValue(props?.next, 'housing_cash_allowance')}
                                tooltip={settings.cashHousingAllowanceLabelHelperText}
                            />
                        </> : null

                }

                {
                    post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.housingValueLabelDB}
                                current={getValue(props.current, 'calculated_housing')}
                                next={getValue(props.next, 'calculated_housing')}
                                tooltip={settings.housingValueLabelDBHelperText}
                                title={settings.calculatedTotalsHeader}
                            />
                        </> : <PaymentItemView
                            label={settings.totalACompPre2018Label}
                            current={getValue(props?.current, 'tac')}
                            next={getValue(props?.next, 'tac')}
                            tooltip={settings.totalACompPre2018HelperText}
                            title={settings.calculatedTotalsHeader}
                        />

                }
                {
                    post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.totalAssessableCompensationLabelDB}
                                current={getValue(props?.current, 'tac')}
                                next={getValue(props?.next, 'tac')}
                                tooltip={settings.totalAssessableCompensationLabelDBHelperText}
                            />
                        </> : <PaymentItemView
                            label={settings.totalRSVPPre2018Label}
                            current={getValue(props?.current, 'tac_dc_rsvp')}
                            next={getValue(props?.next, 'tac_dc_rsvp')}
                            tooltip={settings.totalRSVPPre2018HelperText}
                        />

                }
                {
                    post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.housingValueLabelDC}
                                current={getValue(props.current, 'calculated_housing_dc_rsvp')}
                                next={getValue(props.next, 'calculated_housing_dc_rsvp')}
                                tooltip={settings.housingValueLabelDCHelperText}
                            />
                        </> : null

                }
                {
                    post2018Data ?
                        <>
                            <PaymentItemView
                                label={settings.totalAssessableCompensationLabelDC}
                                current={getValue(props?.current, 'tac_dc_rsvp')}
                                next={getValue(props?.next, 'tac_dc_rsvp')}
                                tooltip={settings.totalAssessableCompensationLabelDCHelperText}
                            />
                        </> : null

                }
                {
                    post2018Data && !props.editDisabled ? renderEdit() : null
                }
                <Pagination name={props.paginationName} totalPages={props.totalPages} />
            </div>

        </>
    );
};